/* import __COLOCATED_TEMPLATE__ from './pronunciation-manager.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import type FinVoicePronunciation from 'embercom/models/fin-voice/pronunciation';
import type AudioPreviewService from 'embercom/services/audio-preview';
import type { PronunciationFormData } from './pronunciation-side-drawer';

interface Signature {
  Args: {};
  Element: HTMLElement;
}

export default class PronunciationManager extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;
  @service declare audioPreview: AudioPreviewService;

  @tracked pronunciations: FinVoicePronunciation[] = [];
  @tracked isLoading = false;
  @tracked searchQuery = '';
  @tracked isEditPanelOpen = false;
  @tracked editingPronunciation: FinVoicePronunciation | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.loadPronunciations();
  }

  willDestroy() {
    super.willDestroy();
    this.audioPreview.stop();
  }

  get isPlayingAudio(): boolean {
    return this.audioPreview.isPlaying;
  }

  get filteredPronunciations(): FinVoicePronunciation[] {
    if (!this.searchQuery.trim()) {
      return this.pronunciations;
    }
    let query = this.searchQuery.toLowerCase();
    return this.pronunciations.filter(
      (p) =>
        p.term.toLowerCase().includes(query) ||
        p.defaultPronunciation.toLowerCase().includes(query),
    );
  }

  hasOverrides(pronunciation: FinVoicePronunciation): boolean {
    return !!pronunciation.languageOverrides?.length;
  }

  async loadPronunciations() {
    try {
      this.isLoading = true;
      let models = await this.store.findAll('fin-voice/pronunciation');
      this.pronunciations = models.slice();
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.pronunciation-manager.load-error'),
      );
    } finally {
      this.isLoading = false;
    }
  }

  @action
  updateSearchQuery(event: Event) {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  @action
  openNewTermPanel() {
    this.editingPronunciation = null;
    this.isEditPanelOpen = true;
  }

  @action
  openEditPanel(pronunciation: FinVoicePronunciation) {
    this.editingPronunciation = pronunciation;
    this.isEditPanelOpen = true;
  }

  @action
  closeEditPanel() {
    this.isEditPanelOpen = false;
    this.editingPronunciation = null;
  }

  @action
  async playPronunciation(pronunciation: FinVoicePronunciation, event?: Event) {
    event?.stopPropagation();
    await this.audioPreview.play(
      pronunciation.term,
      pronunciation.defaultPronunciation,
      pronunciation.defaultTranslationType,
    );
  }

  @action
  async handleSave(formData: PronunciationFormData) {
    try {
      if (this.editingPronunciation === null) {
        let newModel = this.store.createRecord('fin-voice/pronunciation', {
          term: formData.term,
          defaultPronunciation: formData.defaultPronunciation,
          defaultTranslationType: formData.defaultTranslationType,
          languageOverrides: formData.languageOverrides,
        });
        await newModel.save();
      } else {
        let model = this.editingPronunciation;
        model.term = formData.term;
        model.defaultPronunciation = formData.defaultPronunciation;
        model.defaultTranslationType = formData.defaultTranslationType;
        model.languageOverrides = formData.languageOverrides;
        await model.save();
      }

      await this.loadPronunciations();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.voice-playground.pronunciation-manager.save-success'),
      );
      this.closeEditPanel();
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.pronunciation-manager.save-error'),
      );
    }
  }

  @action
  async handleDelete(id: string) {
    try {
      let model = this.pronunciations.find((m) => m.id === id);
      if (model) {
        await model.destroyRecord();
        await this.loadPronunciations();
      }
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.voice-playground.pronunciation-manager.delete-success'),
      );
      this.closeEditPanel();
    } catch (_e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.pronunciation-manager.delete-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::PronunciationManager': typeof PronunciationManager;
    'ai-agent/voice-playground/pronunciation-manager': typeof PronunciationManager;
  }
}
